import { NextPage } from "next";
import { useIntl } from "react-intl";
import router, { useRouter } from "next/router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Link from "next/link";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { PageProps } from "@/pages/_app";
import { routes } from "@/routes/routes";
import Layout from "@/components/layout/Layout";
import { AngleDownIcon } from "@/public/icons/AngleDownIcon";
import { AlertIcon } from "@/public/icons/AlertIcon";
import { Loader2 } from "@/components/common/loader/Loader";
import { CrossIcon } from "@/public/icons/CrossIcon";
import { IDIcon } from "@/public/icons/IDIcon";
import { TiresIcon } from "@/public/icons/TiresIcon";
import { CreditCardIcon } from "@/public/icons/CreditCardIcon";
import { HomeIcon } from "@/public/icons/HomeIcon";
import { CtaButton } from "@/components/common/ctaButton/CtaButton";
import { DocumentIcon } from "@/public/icons/DocumentIcon";
import {
  ActiveOption,
  fetchActiveOptions,
} from "@/components/pages/additionalOptions/helpers/useFetchOptions";
import { DescList } from "@/components/pages/additionalOptions/helpers/temporaryAdditionnalOptionData";
import { PhoneIcon } from "@/public/icons/PhoneIcon";
import { MailIcon } from "@/public/icons/MailIcon";
import { sendFetchRequest } from "@/utils/sendFetchRequest";
import { NetworkError } from "@/interfaces/errors/networkError";
import { useFetchRentalInformations } from "../hooks/useFetchRentalInformations";
import styles from "./Informations.module.scss";
import { CustomerCard } from "./customerCard/CustomerCard";

type ConnectError = {
  errorCode: number;
  message: string;
  requestId: string;
  errorDetailsObject: null;
};

dayjs.extend(customParseFormat);

const cleanRentalID = (value: string) => value.replace(/[^a-zA-Z0-9]/g, "");

type InformationsProps = PageProps & {
  slug: string;
  defaultCanonicalRoute: string;
};

export const Informations: NextPage<InformationsProps> = ({
  layout,
  slug,
  defaultCanonicalRoute,
}: InformationsProps) => {
  const intl = useIntl();
  const rout = useRouter();

  const [toComplete, setToComplete] = useState<string[]>([]);
  const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false);
  const [daysUntil, setDaysUntil] = useState<number>(-1);
  const [activeOptions, setActiveOptions] = useState<ActiveOption[]>([]);

  const [slugChecking, setSlugChecking] = useState<string>("");
  const [isFirstFetchLoading, setIsFirstFetchLoading] =
    useState<boolean>(false);

  useEffect(() => {
    if (slugChecking === "") {
      if (slug === "email") {
        const queryString = rout.asPath.split("?")[1];
        const queryParams: Record<string, string> = {};
        queryString.split("&").forEach((param) => {
          const [key, value] = param.split("=");
          queryParams[key] = decodeURIComponent(value);
        });

        const email = queryParams["email"];
        const bookingId = queryParams["reservationId"];
        const isLinkBooking = queryParams["link"];
        const plateNumber = queryParams["PlateNumber"];

        if (email && bookingId) {
          setIsFirstFetchLoading(true);
          const encodedEmail = encodeURIComponent(email);
          const cleanBookingId = cleanRentalID(bookingId);
          const bookingReferenceTypeParam = plateNumber
            ? "&BookingReferenceType=PlateNumber"
            : "";

          sendFetchRequest(
            `/customers/v1/checkin/checkCustomerEmailAndBooking?documentId=${cleanBookingId}&SearchValue=${encodedEmail}&FieldType=email&link=${isLinkBooking}${bookingReferenceTypeParam}`
          )
            .then((response: string) => {
              void router.push({
                pathname: routes.myLocation(response),
              });
            })
            .catch((e: NetworkError<ConnectError>) => {
              console.error("First fetch error:", e.message);
              setIsFirstFetchLoading(false);
            });
        } else {
          console.error("Missing email or reservationId in query parameters.");
        }
      } else {
        setSlugChecking(slug);
      }
    }
  }, [slug, rout.asPath, slugChecking]);

  const {
    rentalInformations,
    error,
    isLoading: isRentalInformationsLoading,
  } = useFetchRentalInformations(slugChecking);

  const getOptionLabel = (code: string) => {
    const index = DescList.findIndex((desc) => code.includes(desc.code));

    return DescList[index]?.title.toLowerCase() ?? "";
  };

  const contactBy = (type: "email" | "phone") => {
    if (type === "email") {
      window.location.href = `mailto:${rentalInformations?.agencyEmail ?? ""}`;
    } else {
      window.location.href = `tel:${rentalInformations?.agencyPhone ?? ""}`;
    }
  };

  useEffect(() => {
    if (
      rentalInformations &&
      !isRentalInformationsLoading &&
      !isFirstFetchLoading
    ) {
      if (error !== undefined) {
        console.error("Rental informations fetch error:", error);
        void router.push({
          pathname: routes.checkIn,
        });

        return;
      }

      const tmp: string[] = [];

      if (
        rentalInformations.customerLogged === "Customer" &&
        rentalInformations.customer.isDocumentsMissing &&
        !rentalInformations.customer.isCustomerTheDriver
      ) {
        tmp.push(intl.formatMessage({ id: "clientDocs" }));
      }

      rentalInformations.drivers?.forEach((d) => {
        if (d.isDocumentsMissing) {
          tmp.push(intl.formatMessage({ id: "driverDocs" }));
        }
      });

      setToComplete(tmp);

      const diff = dayjs(rentalInformations.startDate).diff(
        dayjs(),
        "days",
        true
      );
      const isSame = dayjs(rentalInformations.startDate).isSame(dayjs(), "day");
      setDaysUntil(Math.ceil(isSame ? 0 : diff > 0 ? diff : -1));

      fetchActiveOptions(rentalInformations.bookingId)
        .then((response) => setActiveOptions(response))
        .catch((e) => {
          console.error("Fetch active options error:", e);
        });
    }
  }, [
    rentalInformations,
    error,
    isRentalInformationsLoading,
    isFirstFetchLoading,
    intl,
  ]);

  const isLoading = isFirstFetchLoading || isRentalInformationsLoading;

  return (
    <Layout
      layout={layout}
      defaultCanonicalRoute={defaultCanonicalRoute}
      title={intl.formatMessage({ id: "checkInInformations" })}
      breadcrumbs={[
        { title: intl.formatMessage({ id: "home" }), url: "/" },
        {
          title: intl.formatMessage({ id: `checkInInformations` }),
          url: defaultCanonicalRoute,
        },
      ]}
    >
      <div className={styles.header}>
        <Link href={rout.asPath}>
          <a className={styles.previous}>
            <AngleDownIcon colorClass={styles.angle} width="16" height="16" />
          </a>
        </Link>
        {!isLoading && !showMoreInfo && rentalInformations && (
          <div className={styles.label}>
            Location {rentalInformations.bookingId}
          </div>
        )}
      </div>

      {!isLoading && !showMoreInfo && rentalInformations && (
        <>
          <div className={styles.section}>
            <div className={styles.informations}>
              {daysUntil >= 0 && (
                <div className={styles.until}>
                  {daysUntil > 0
                    ? intl.formatMessage(
                        { id: "daysUntil" },
                        { days: daysUntil }
                      )
                    : intl.formatMessage({ id: "pickupNow" })}
                </div>
              )}
              <div className={styles.pickUp}>
                <b>{intl.formatMessage({ id: "pickupTitle" })} :</b>{" "}
                {dayjs(rentalInformations.startDate).format(
                  "dddd DD/MM/YYYY - HH:mm"
                )}
              </div>

              <div className={styles.address}>
                {`${rentalInformations.agencyAddress.street}, ${rentalInformations.agencyAddress.zipCode} ${rentalInformations.agencyAddress.city}`}
              </div>
            </div>

            {toComplete.length > 0 && (
              <div className={styles.toComplete}>
                <div className={styles.label}>
                  <span className={styles.alertIcon}>
                    <AlertIcon />
                  </span>{" "}
                  {intl.formatMessage({ id: "toComplete" })}
                </div>
                <div className={styles.missing}>{toComplete.join(" - ")}</div>
              </div>
            )}

            <div className={styles.contractInformations}>
              <div className={styles.title}>
                {intl.formatMessage({ id: "contract" })}
              </div>

              {rentalInformations.customerLogged === "Customer" &&
                !rentalInformations.customer.isCustomerTheDriver && (
                  <CustomerCard
                    name={rentalInformations.customer.name}
                    isDocumentsMissing={
                      !!rentalInformations.customer.isDocumentsMissing
                    }
                    slug={slug}
                    label={intl.formatMessage({ id: "client" })}
                    cacheId={rentalInformations.customer.redirectCacheId}
                    type="customer"
                  />
                )}

              {rentalInformations.drivers?.map((driver) => (
                <CustomerCard
                  key={driver.name}
                  name={driver.name}
                  isDocumentsMissing={!!driver.isDocumentsMissing}
                  label={intl.formatMessage({
                    id: driver.isMainDriver ? "mainDriver" : "driver",
                  })}
                  cacheId={driver.redirectCacheId}
                  type="driver"
                  slug={slug}
                />
              ))}

              {rentalInformations.additionalDrivers?.map((driver) => (
                <CustomerCard
                  key={driver.name}
                  name={driver.name}
                  isDocumentsMissing={!!driver.isDocumentsMissing}
                  label={intl.formatMessage({
                    id: driver.isMainDriver ? "mainDriver" : "additionalDriver",
                  })}
                  cacheId={driver.redirectCacheId}
                  type="additionalDriver"
                  slug={slug}
                />
              ))}

              <div className={styles.card}>
                <div className={styles.label}>
                  {intl.formatMessage({ id: "deposit" })}
                </div>
                {rentalInformations.guaranteeDeposit !== null ? (
                  <div className={styles.description}>
                    {rentalInformations.guaranteeDeposit}€
                  </div>
                ) : (
                  <div className={styles.description}>
                    {intl.formatMessage({ id: "amountInAgency" })}
                  </div>
                )}
                <div className={styles.noAction}>
                  <span className={styles.alertIcon}>
                    <AlertIcon />
                  </span>
                  {intl.formatMessage({ id: "toDoInAgency" })}
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.label}>
                  {intl.formatMessage({ id: "vehicle" })}
                </div>
                <div className={styles.description}>
                  {intl.formatMessage(
                    { id: "categoryValue" },
                    {
                      cat: rentalInformations.vehicleId,
                      label: rentalInformations.category,
                    }
                  )}
                </div>
                <div className={classNames(styles.action, styles.disabled)}>
                  {/* Placeholder for any future actions */}
                </div>
              </div>
            </div>

            <div className={styles.importantInformations}>
              <div className={styles.title}>
                {intl.formatMessage({ id: "importantInformationsTitle" })}
              </div>
              <div className={styles.description}>
                {intl.formatMessage({ id: "importantInformationsDesc" })}
              </div>
              <div
                className={styles.about}
                onClick={() => setShowMoreInfo(true)}
              >
                {intl.formatMessage({ id: "knowMore" })}
              </div>
            </div>

            <div className={styles.rental}>
              <div className={styles.title}>
                {intl.formatMessage({ id: "myRental" })}
              </div>
              <div className={styles.details}>
                <div className={styles.subtitle}>
                  {intl.formatMessage({ id: "details" })}
                </div>
                <div className={styles.duration}>
                  <div className={styles.dates}>
                    {intl.formatMessage({ id: "from" })}{" "}
                    {dayjs(rentalInformations.startDate).format("DD/MM/YYYY")}{" "}
                    {intl.formatMessage({ id: "to" })}{" "}
                    {dayjs(rentalInformations.endDate).format("DD/MM/YYYY")}
                  </div>
                  <div className={styles.values}>
                    <div className={styles.value}>
                      {intl.formatMessage(
                        { id: "days" },
                        {
                          days: Math.ceil(
                            dayjs(rentalInformations.endDate).diff(
                              rentalInformations.startDate,
                              "days",
                              true
                            )
                          ),
                        }
                      )}
                    </div>
                    <div className={styles.price}>
                      {rentalInformations.priceTtc} €
                    </div>
                  </div>
                </div>
                {activeOptions.length > 0 && (
                  <div className={styles.options}>
                    <div className={styles.label}>
                      {intl.formatMessage({ id: "options" })}
                    </div>

                    {activeOptions.map((opt) => (
                      <div className={styles.option} key={opt.id}>
                        <div className={styles.value}>
                          {getOptionLabel(opt.id)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.contact}>
            <div className={styles.container}>
              <div className={styles.title}>
                {intl.formatMessage({ id: "contactAgency" })}
              </div>

              <div
                className={styles.email}
                onClick={() => contactBy("email")}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    contactBy("email");
                  }
                }}
              >
                <div className={styles.icon}>
                  <MailIcon />
                </div>
                <div className={styles.label}>
                  {intl.formatMessage({ id: "contactByMail" })}
                </div>
              </div>

              <div
                className={styles.phone}
                onClick={() => contactBy("phone")}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    contactBy("phone");
                  }
                }}
              >
                <div className={styles.icon}>
                  <PhoneIcon />
                </div>
                <div className={styles.label}>
                  {(
                    rentalInformations.agencyPhone?.match(/.{1,2}/g) ?? []
                  ).join(".")}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {isLoading && (
        <div className={styles.loading}>
          <Loader2 size={150} />
        </div>
      )}

      {showMoreInfo && (
        <div className={styles.moreInformations}>
          <div className={styles.title}>
            <span className={styles.titleValue}>
              {intl.formatMessage({ id: "importantInformationsTitle" })}
            </span>
            <div
              className={styles.close}
              onClick={() => setShowMoreInfo(false)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  setShowMoreInfo(false);
                }
              }}
            >
              <CrossIcon color="#000" size={37} />
            </div>
          </div>
          <div className={styles.subtitle}>
            {intl.formatMessage({ id: "importantInformationsSubtitle" })}
          </div>
          <div className={styles.list}>
            {/* ID Passport */}
            <div className={classNames(styles.item, styles.centered)}>
              <div className={styles.icon}>
                <IDIcon width={72} height={48} />
              </div>
              <div className={styles.body}>
                <div className={styles.desc}>
                  {intl.formatMessage(
                    { id: "idPassportDesc" },
                    {
                      br: () => <br />,
                      strong: (chunks) => <b>{chunks}</b>,
                    }
                  )}
                </div>
              </div>
            </div>

            <div className={classNames(styles.item, styles.centered)}>
              <div className={styles.icon}>
                <TiresIcon width={59} height={71} />
              </div>
              <div className={styles.body}>
                <div className={styles.desc}>
                  {intl.formatMessage(
                    { id: "driversLicenseDesc" },
                    {
                      br: () => <br />,
                      strong: (chunks) => <b>{chunks}</b>,
                    }
                  )}
                </div>
              </div>
            </div>

            <div className={classNames(styles.item, styles.centered)}>
              <div className={styles.icon}>
                <HomeIcon width={79} height={69} />
              </div>
              <div className={styles.body}>
                <div className={styles.label}>
                  {intl.formatMessage({ id: "addressProof" })}
                </div>
                <div className={styles.desc}>
                  {intl.formatMessage({ id: "addressProofDesc" })}
                </div>
              </div>
            </div>

            <div className={styles.item}>
              <div className={styles.icon}>
                <CreditCardIcon width={72} height={48} />
              </div>
              <div className={classNames(styles.body, styles.top)}>
                <div className={styles.desc}>
                  {intl.formatMessage(
                    { id: "creditCardDesc" },
                    {
                      br: () => <br />,
                      strong: (chunks) => <b>{chunks}</b>,
                    }
                  )}
                </div>
              </div>
            </div>

            <div className={styles.item}>
              <div className={styles.icon}>
                <DocumentIcon width={58} height={69} />
              </div>
              <div className={classNames(styles.body, styles.top)}>
                <div className={styles.desc}>
                  {intl.formatMessage(
                    { id: "b2bDocuments" },
                    {
                      br: () => <br />,
                      strong: (chunks) => <b>{chunks}</b>,
                    }
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.close}>
            <CtaButton
              label={intl.formatMessage({ id: "close" })}
              onClick={() => setShowMoreInfo(false)}
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

// http://localhost:3000/check-in/email?reservationId=0019R226725&email=marie-julie.ricard@rentacar.fr
